/**
 * 🗑️ CorbeilleArticlesDialog — [Phase 19A]
 *
 * Modal listant les articles soft-deleted de l'entrepôt avec actions :
 *   - Restaurer (remet active=true, conserve tous les attributs)
 *   - Supprimer définitivement (hard-delete réel, irréversible)
 *
 * Accessible owner uniquement (backend = `proprietaire/admin_general/adjoint_admin`).
 * Pas de cleanup auto : la corbeille reste permanente jusqu'à action manuelle.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trash2, RotateCcw, Loader2, AlertTriangle, Package } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function CorbeilleArticlesDialog({ open, onClose, onRestored }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [confirmHard, setConfirmHard] = useState(null); // {id, name}

  const fetchCorbeille = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${api.baseUrl}/inventory/products/corbeille`);
      setItems(data?.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur chargement corbeille';
      toast.error(typeof msg === 'string' ? msg : 'Erreur chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchCorbeille();
  }, [open, fetchCorbeille]);

  const restaurer = async (item) => {
    setBusyId(item.id);
    try {
      await axios.post(`${api.baseUrl}/inventory/products/${item.id}/restaurer`);
      toast.success(`✅ « ${item.name || 'Produit'} » restauré`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      if (typeof onRestored === 'function') onRestored();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur restauration');
    } finally {
      setBusyId(null);
    }
  };

  const supprimerDefinitif = async (item) => {
    setBusyId(item.id);
    try {
      await axios.delete(`${api.baseUrl}/inventory/products/${item.id}/definitif`);
      toast.success(`🗑️ « ${item.name || 'Produit'} » supprimé définitivement`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression définitive');
    } finally {
      setBusyId(null);
      setConfirmHard(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso.slice(0, 16); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
        <DialogContent
          className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="corbeille-articles-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-slate-500" />
              Corbeille — Articles supprimés
              {items.length > 0 && (
                <span className="text-xs font-normal text-slate-500">({items.length})</span>
              )}
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Restaurez un article pour le faire réapparaître dans l&apos;entrepôt avec tous ses
              attributs préservés (code-barres, normes, etc.), ou supprimez-le définitivement.
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-2" data-testid="corbeille-list">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">La corbeille est vide.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 hover:bg-white transition"
                    data-testid={`corbeille-item-${item.id}`}
                  >
                    <Package className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate" data-testid={`corbeille-name-${item.id}`}>
                        {item.name || item.nom || '(sans nom)'}
                      </p>
                      <div className="text-[11px] text-slate-500 mt-0.5 space-x-2">
                        {item.code_produit && <span className="font-mono">{item.code_produit}</span>}
                        {item.category && <span>· {item.category}</span>}
                        {item.quantity != null && <span>· qté avant : {item.quantity}</span>}
                      </div>
                      <div className="text-[11px] text-slate-600 mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>🕒 Supprimé le <strong>{formatDate(item.supprime_at)}</strong></span>
                        {item.supprime_par_nom && (
                          <span>👤 Par <strong>{item.supprime_par_nom}</strong></span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => restaurer(item)}
                        disabled={busyId === item.id}
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 h-8 text-xs"
                        data-testid={`corbeille-restore-${item.id}`}
                      >
                        {busyId === item.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Restaurer
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmHard({ id: item.id, name: item.name })}
                        disabled={busyId === item.id}
                        className="text-red-700 border-red-300 hover:bg-red-50 h-8 text-xs"
                        data-testid={`corbeille-hard-delete-${item.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Définitif
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="corbeille-close">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sous-dialog confirmation suppression définitive */}
      <Dialog open={!!confirmHard} onOpenChange={(v) => !v && setConfirmHard(null)}>
        <DialogContent className="sm:max-w-md z-[80]" data-testid="confirm-hard-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Supprimer définitivement ?
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 text-sm text-slate-700">
            <p>
              Vous êtes sur le point de supprimer définitivement :
              <br />
              <strong className="text-slate-900">« {confirmHard?.name || 'Produit'} »</strong>
            </p>
            <p className="mt-3 text-red-700 font-semibold">
              ⚠️ Cette action est <u>irréversible</u>. L&apos;article et son historique de suppression disparaîtront pour de bon.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmHard(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmHard && supprimerDefinitif(confirmHard)}
              disabled={busyId === confirmHard?.id}
              data-testid="confirm-hard-delete-btn"
            >
              {busyId === confirmHard?.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Supprimer pour de bon
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
